import React from "react";
import { Checkbox } from "../../../components/ui";
import { EXTERNAL_LINKS } from "../../../routes/paths";
import type { ConsentDocumentType } from "../types/consent.types";

/**
 * The DPDP terms themselves, and the ticks that agree to them.
 *
 * Shared by the two screens that ask: the sign-up consent screen, which asks
 * before an account exists, and the gate a returning user meets when a document
 * has been published or republished since they last agreed. They have to state
 * the terms in exactly the same words - it is the same agreement - so the rows
 * live here rather than being written out twice and drifting apart.
 */

/** One tick, in the order the rows are shown. */
export type ConsentRowKey = "terms" | "dpa" | "age";

export interface ConsentRow {
  key: ConsentRowKey;
  /**
   * The backend documents this row stands for.
   *
   * The first row covers two. Agreeing to the Terms of use and the Privacy
   * policy is one decision as far as the user is concerned, and four separate
   * boxes would be a worse screen for no more consent.
   */
  documents: readonly ConsentDocumentType[];
  label: React.ReactNode;
  description?: React.ReactNode;
}

/** One size and colour for every row, over the form default the shared
 *  checkbox uses everywhere else. */
const ROW_LABEL = "text-[15px] 3xl:text-[15px] text-[#1E1F24]";

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-[#00C2CB] font-medium hover:underline"
  >
    {children}
  </a>
);

export const CONSENT_ROWS: readonly ConsentRow[] = [
  {
    key: "terms",
    documents: ["TERMS_OF_USE", "PRIVACY_POLICY"],
    label: (
      <>
        I agree to the{" "}
        <ExternalLink href={EXTERNAL_LINKS.termsOfUse}>
          Terms of use
        </ExternalLink>{" "}
        and{" "}
        <ExternalLink href={EXTERNAL_LINKS.privacyPolicy}>
          Privacy policy.
        </ExternalLink>
      </>
    ),
  },
  {
    key: "dpa",
    documents: ["DATA_PROCESSING_AGREEMENT"],
    label: "I agree to the Data Processing Agreement.",
    description: (
      <>
        I&rsquo;ll make sure my clients are happy to share any health data I
        handle.{" "}
        <ExternalLink href={EXTERNAL_LINKS.dataProcessingAgreement}>
          Read DPA
        </ExternalLink>
      </>
    ),
  },
  {
    key: "age",
    documents: ["AGE_ATTESTATION"],
    label: <>I confirm I&rsquo;m 18 years of age or older.</>,
    description: "Naurist is for practitioners aged 18 and above.",
  },
];

/**
 * The rows covering at least one of the given documents, in display order.
 *
 * What the backend says is outstanding is what gets asked about: a user who is
 * only being shown a republished Privacy policy should not be made to confirm
 * their age a second time to get past it.
 */
export const consentRowsFor = (
  types: readonly ConsentDocumentType[]
): ConsentRow[] =>
  CONSENT_ROWS.filter((row) =>
    row.documents.some((document) => types.includes(document))
  );

/** Every document type the given rows stand for. */
export const documentsOf = (
  rows: readonly ConsentRow[]
): ConsentDocumentType[] => rows.flatMap((row) => [...row.documents]);

export const ConsentTerms: React.FC<{
  rows: readonly ConsentRow[];
  agreed: Readonly<Partial<Record<ConsentRowKey, boolean>>>;
  onToggle: (key: ConsentRowKey, checked: boolean) => void;
}> = ({ rows, agreed, onToggle }) => (
  <div className="mt-4 divide-y divide-[#EDF0F2] border-t border-[#EDF0F2]">
    {rows.map((row) => (
      <div key={row.key} className="py-4">
        <Checkbox
          checked={!!agreed[row.key]}
          onCheckedChange={(checked) => onToggle(row.key, checked)}
          labelClassName={ROW_LABEL}
          label={row.label}
          description={row.description}
        />
      </div>
    ))}
  </div>
);
